import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import DialogAdaptive from '../../../components/common/DialogAdaptive';
import DialogContentAdaptive from '../../../components/common/DialogContentAdaptive';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import type { OnlyPromoteLink } from '../../../hooks/useOnlyPromoteLinks';

const sortLinksStable = (items: OnlyPromoteLink[]): OnlyPromoteLink[] =>
    [...items].sort((a, b) => {
        const d = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (d !== 0) return d;
        return a.id - b.id;
    });

const PLATFORM_LABELS: Record<string, string> = {
    onlyfans: 'OnlyFans',
    fansly: 'Fansly',
    instagram: 'Instagram',
    twitter: 'X / Twitter',
    tiktok: 'TikTok',
    snapchat: 'Snapchat',
    youtube: 'YouTube',
    twitch: 'Twitch',
    reddit: 'Reddit',
    telegram: 'Telegram',
    discord: 'Discord',
    linktree: 'Linktree',
    amazon: 'Amazon',
    cashapp: 'Cash App',
    other: 'Other',
};

const SortableLinkRow = ({ link }: { link: OnlyPromoteLink }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
        id: link.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            variant="outlined"
            sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: 1,
            }}
        >
            <Box
                ref={setActivatorNodeRef}
                component="span"
                {...attributes}
                {...listeners}
                sx={{
                    display: 'inline-flex',
                    cursor: 'grab',
                    color: 'text.secondary',
                    touchAction: 'none',
                    '&:active': { cursor: 'grabbing' },
                }}
            >
                <DragIndicatorIcon fontSize="small" />
            </Box>
            <Stack direction="column" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {link.name}
                    </Typography>
                    {!link.active && <Chip label="Inactive" size="small" variant="outlined" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {PLATFORM_LABELS[link.platform] || link.platform}
                </Typography>
            </Stack>
        </Paper>
    );
};

interface LinkReorderModalProps {
    open: boolean;
    onClose: () => void;
    links: OnlyPromoteLink[];
    updateLink: (args: { linkId: number; payload: { sortOrder: number } }) => Promise<unknown>;
    onSaved: () => Promise<void>;
}

const LinkReorderModal = ({ open, onClose, links, updateLink, onSaved }: LinkReorderModalProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [ordered, setOrdered] = useState<OnlyPromoteLink[]>([]);
    const [saving, setSaving] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        setOrdered(sortLinksStable(links));
    }, [open, links]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = ordered.findIndex((i) => i.id === active.id);
        const newIndex = ordered.findIndex((i) => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        setOrdered((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            for (let i = 0; i < ordered.length; i += 1) {
                const link = ordered[i];
                // eslint-disable-next-line no-await-in-loop
                await updateLink({ linkId: link.id, payload: { sortOrder: i } });
            }
            await onSaved();
            enqueueSnackbar('Link order updated', { variant: 'success' });
            onClose();
        } catch {
            enqueueSnackbar('Failed to update link order.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogAdaptive
            open={open}
            onClose={(_e, _reason) => {
                if (saving) {
                    return;
                }
                onClose();
            }}
            fullScreen={fullScreen}
        >
            <StyledCardTitleBar title="Reorder links" />
            <DialogContentAdaptive>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Drag links to set the order they appear on your OnlyPromote.me page.
                </Typography>
                {ordered.length === 0 ? (
                    <Typography variant="body2">No links to reorder.</Typography>
                ) : (
                    <Box
                        sx={{
                            height: fullScreen ? 'calc(100vh - 220px)' : 'min(56vh, 520px)',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            overscrollBehavior: 'contain',
                            pr: 0.5,
                        }}
                    >
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} autoScroll={false}>
                            <SortableContext items={ordered.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                                <Stack spacing={1}>
                                    {ordered.map((link) => (
                                        <SortableLinkRow key={link.id} link={link} />
                                    ))}
                                </Stack>
                            </SortableContext>
                        </DndContext>
                    </Box>
                )}
            </DialogContentAdaptive>
            <StyledDialogActions sx={{ px: 2, py: 1.5 }}>
                <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <LoadingButton variant="contained" color="primary" onClick={handleSave} loading={saving} disabled={ordered.length === 0}>
                    Save order
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default LinkReorderModal;
